/**
 * 個人レポート（GB人財 / A4横2ページ版）に載せる説明文の「たたき台」。
 *
 * 【編集方針】
 *  - 本ファイルの文字列を書き換えるだけで、画面表示・単票PDF・全員PDFの
 *    3経路すべての文言が変わる（Gb74PersonalReportPdf が既定値として使う）。
 *  - 読み手は社長・管理者。専門用語を避け、「何が分かるか」「どう使うか」が
 *    伝わる長さに収める（長すぎるとページ内の枠に収まらない）。
 *  - 各セクション直下には既に短い注記（※〜）が出ているため、それと重複せず
 *    補足になる内容にする。
 *  - 個人ごとに変わる文言（タイプ名・スタイル・気をつけたいこと本文）は
 *    DB(judgment_patterns の desc11〜47) が情報源。ここには置かない。
 */

/** 2ページ目の解説枠。空文字にすると「（文言調整中）」の枠に戻る。 */
export const PERSONAL_REPORT_NOTES = {
  /**
   * 2ページ目 左下「役割適性について」の表の上に置く導入文。
   *
   * 元は下の roleNote の長文だけだったが、6つの役割それぞれの意味が読み取れないため
   * 表を主役にした（2026-08-23 指示）。この導入文はその長文を2〜3行に要約したもの。
   *
   * 【注意】群のラベルは v2 で「力が出る役割」「以下は参考です」に変わっている。
   * 旧ラベル（向いていそうな役割／補うとよい役割）はレポート上に出ないので使わない。
   */
  roleLead:
    '6つの役割は、日々の仕事で求められる動き方を整理したものです。バーの長さは能力の高低ではなく、その人の中での出やすさを表します。「力が出る役割」は本人に任せると成果が出やすく、それ以外も手順や仕組み、得意な人との組み合わせで補えます。',

  /**
   * 2ページ目 左下「役割適性について」の文章版。
   *
   * 既定では ROLE_DESCRIPTIONS の表を出すため、通常この文章は使わない。
   * Gb74PersonalReportPdf に roleNoteText を明示指定したときだけ、表の代わりに出る。
   */
  roleNote:
    '6つの役割は、日々の仕事で求められる動き方を整理したものです。バーの長さは「その役割に向いているか」の目安で、長いほど自然体で力を発揮しやすいことを表します。「向いていそうな役割」は本人に任せると成果が出やすい領域です。「補うとよい役割」は不得意という意味ではなく、手順やチェックの仕組み、得意な人との組み合わせで十分に補えます。配置や役割分担を考えるときの材料としてお使いください。',

  /** 2ページ目 右下「気をつけたいことについて」 */
  cautionNote:
    'ここに挙げているのは、強みが強く出たときに起こりやすいつまずき方です。思考の型には必ず表と裏があり、得意なことの裏返しとして誰にでも起こります。本人の努力不足ではありません。対処のコツは、意識で直そうとせず「仕組みで防ぐ」ことです。チェック項目を先に決めておく、締切を実際より前倒しで設定する、苦手な観点を得意な人に見てもらう、といった工夫が有効です。',
} as const;

/**
 * 6つの役割の一覧（2ページ目 左下「役割適性について」の表）。
 *
 * 【出どころ】文言DB(GB人財_個人レポート文言DB_v4_3) の PA05。
 *  ・summary … 各エントリの「条件」にある定義（例: 管理統率（チームを率いて成果を出す））
 *  ・detail  … 同ブロックの高適性(A帯)の本文。その役割で力が出る場面を1文で表す
 *  文言DBが正典なので、ここを直すときは PA05 と突き合わせること
 *  （ずれたら personalReportCopy.test.ts が落ちる）。
 *
 * 並び順は RoleName の定義順（点数順ではなく固定）。表は「読み方の説明」であり、
 * 個人ごとに順番が変わると参照しづらいため。
 */
export const ROLE_DESCRIPTIONS: ReadonlyArray<{
  name: string;
  summary: string;
  detail: string;
}> = [
  {
    name: '管理統率',
    summary: 'チームを率いて成果を出す',
    detail: 'まとめ役として、成果まで持っていく',
  },
  {
    name: '変革推進',
    summary: '新しい方向に組織を動かす',
    detail: '新しいやり方を持ち込み、周囲を動かす',
  },
  { name: '安定運用', summary: '決まったことを確実に回す', detail: '手順を崩さず、回し続ける' },
  { name: '協働調整', summary: 'チームの連携を支える', detail: '人と人の間に立ち、連携を保つ' },
  {
    name: '橋渡し',
    summary: '部門間をつなぐ',
    detail: '部署をまたぐ場面で、双方の言い分をつなげる',
  },
  { name: '承継適性', summary: '経営を引き継ぐ', detail: '経営の判断を引き受ける' },
] as const;
